import { createSlice } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';

import { setErrorMessage } from './errors';
import { mapErrorMessage } from '../utils';
import api from '../api';

const initialState = {
  policies: [],
  requestNumber: null,
  registerLoading: false,
  registerError: null,
};

const insurancePolicies = createSlice({
  name: 'insurancePolicies',
  initialState,
  reducers: {
    setInsurancePolicies(state, action) {
      state.policies = action.payload;
    },
    registerInsurancePolicyStart(state) {
      state.registerLoading = true;
      state.registerError = null;
    },
    registerInsurancePolicySuccess(state, action) {
      state.requestNumber = action.payload;
      state.registerLoading = false;
      state.registerError = null;
    },
    registerInsurancePolicyFailure(state) {
      state.registerLoading = false;
    },
  },
});

export const {
  setInsurancePolicies,
  registerInsurancePolicyStart,
  registerInsurancePolicySuccess,
  registerInsurancePolicyFailure,
} = insurancePolicies.actions;

export const fetchInsurancePolicies = () => async (dispatch, getState) => {
  try {
    const { auth } = getState();
    const { data } = await api.get('/seguros/ch/PDS', {
      headers: { Authorization: `Bearer ${auth.token}` },
    });

    const insurancePolicies = data.insurance.filter((i) => i !== 'insurance');
    dispatch(setInsurancePolicies(insurancePolicies));
  } catch (error) {
    return;
  }
};

export const registerInsurancePolicy = () => async (dispatch, getState) => {
  try {
    dispatch(registerInsurancePolicyStart());
    const {
      user,
      auth,
      insurances,
      insurancePlans,
      payments,
      techModel,
      hogarModel,
      lifeExtraData,
      taxes,
      activity,
    } = getState();
    const { currentAddress, contactDetails } = user;
    const {
      department,
      floor,
      locality,
      state,
      street,
      streetNumber,
      zipCode,
    } = currentAddress || {};
    const { insuranceProductId, insuranceCompany, insuranceName } =
      insurances.selected;

    const companyDesc = insuranceCompany.shortDesc;
    let productDes = insuranceCompany.products[0].longDesc;

    const hasSubProducts = insuranceCompany.products.length > 1;

    if (hasSubProducts) {
      const [selectedProduct] = insuranceCompany.products.filter(
        (p) => p.productId === insuranceProductId
      );
      productDes = selectedProduct && selectedProduct.longDesc;
    }

    let data = {
      generic: {
        identificationBody: 'FULL',
      },
      additionalBeneficiaries: {},
      beneficiaries: {},
      contactTypes: {
        contactType: [
          {
            contactTypeId: 33,
            description: contactDetails.phone,
          },
          {
            contactTypeId: 66,
            description: contactDetails.email,
          },
        ],
      },
      customerOperations: {},
      insuranceCoverages: {},
      insuranceItems: {},
      productFamilies: {},
      insuranceCompany: {
        products: {
          product: [{ product: productDes }],
        },
        company: companyDesc,
      },
      insurancePlan: {
        planId: insurancePlans.selected.planId,
      },
      insuranceProductType: {},
      isoProductType: '',
      systemUser: {
        electronicsContact: {
          emailAddressComplete: contactDetails.email,
        },
      },
    };

    const { paymentType, paymentData } = payments.paymentMethod;
    if (paymentType === 'accounts') {
      data['account'] = {
        bukNumber: paymentData.bukNumber,
        product: {
          isoProduct: paymentData.product.isoProduct,
        },
      };
      data['insurance'] = {
        insurancePaymentMethod: 'C',
      };
    } else if (paymentType === 'creditCard') {
      data['creditCard'] = {
        creditCardAccount: {
          operationId: paymentData.creditCardAccount.operationId,
        },
      };
      data['insurance'] = {
        insurancePaymentMethod: 'T',
      };
    }

    if (insuranceName === 'HOGAR') {
      // eliminamos los ultimos 2 elementos del array porque sino rompe el alta
      const extraData = hogarModel.hogarModel.slice(0, -2);

      data['extraData'] = {
        parameter: extraData,
      };
      data['riskAddress'] = {
        address: {
          apartment: department,
          district: '',
          floor: floor,
          locality: locality && locality.trim(),
          neighborhood: '',
          provinceCode: state.value,
          street: street && street.trim(),
          streetNumber: streetNumber,
          zipCode: zipCode,
        },
      };
    } else if (insuranceName === 'TECNO') {
      data['extraData'] = {
        parameter: techModel.techModel,
      };
    } else if (insuranceName === 'VIDA INDIVIDUAL') {
      data['extraData'] = {
        parameter: lifeExtraData.lifeExtraData,
      };
    } else if (insuranceName === 'INTEGRAL DE COMERCIO') {
      data['extraData'] = {
        parameter: [
          {
            name: 12,
            value: taxes.taxes.condition2,
          },
          {
            name: 19,
            value: taxes.taxes.condition,
          },
          {
            name: 3323,
            value: activity.activity.selected,
          },
          {
            name: 3340,
            value: 'S',
          },
          {
            name: 3341,
            value: 'S',
          },
          {
            name: 3342,
            value: 'S',
          },
          {
            name: 3343,
            value: 'S',
          },
          {
            name: 3344,
            value: 'n',
          },
          {
            name: 3345,
            value: 'S',
          },
          {
            name: 3346,
            value: 'S',
          },
        ],
      };
      data['riskAddress'] = {
        address: {
          apartment: department,
          district: '',
          floor: floor,
          locality: locality && locality.trim(),
          neighborhood: '',
          provinceCode: state.value,
          street: street && street.trim(),
          streetNumber: streetNumber,
          zipCode: zipCode,
        },
      };
    }

    const response = await api.put(
      '/seguros/ch/PDS',
      {},
      {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
        data: data,
      }
    );

    if (response.data && response.data.requestNumber) {
      dispatch(registerInsurancePolicySuccess(response.data.requestNumber));
      dispatch(push(`/felicitaciones`));
    } else {
      dispatch(registerInsurancePolicyFailure());
      dispatch(setErrorMessage(mapErrorMessage('systemError')));
      dispatch(push('/error'));
    }
  } catch (error) {
    dispatch(registerInsurancePolicyFailure());
    dispatch(setErrorMessage(mapErrorMessage('systemError')));
    dispatch(push('/error'));
    return;
  }
};

export default insurancePolicies.reducer;
